import React, { useEffect, useMemo, useState } from "react";
import { useIntl } from "react-intl";
import {
  Button,
  Checkbox,
  Dropdown,
  Form,
  FormField,
  FormGroup,
  Icon,
  Input,
} from "semantic-ui-react";

// Common
import ButtonLoadCheck from "react-lib/appcon/common/ButtonLoadCheck";

// Utils
import { MONTH_EN_OPTION } from "react-lib/utils/dateUtils";

// Interface
import {
  CARD_NAME,
  PickedProps,
  RunSequence,
  SetProp,
  RestrictGenderOptions,
  LeaveTypeListApiType,
  LeaveTypeConditionType,
} from "./sequence/LeavePolicy";

type LeaveSettingsPanelProps = {
  runSequence: RunSequence;
  setProp: SetProp;
  onClose?: () => void;
  data?: LeaveTypeListApiType | null;
} & PickedProps;

type DependOnExpDetailProps = {
  value: LeaveTypeConditionType[];
  onChange: (newValue: LeaveTypeConditionType[]) => void;
};

const CardLeaveSettingsPanel = (props: LeaveSettingsPanelProps) => {
  const [formState, setFormState] = useState<LeaveTypeListApiType>({});

  useEffect(() => {
    if (props.data) {
      setFormState({ ...props.data });
    } else {
      setFormState({});
    }
  }, [props.data]);

  const handleClose = () => {
    props.onClose?.();

    // clear data
    setFormState({});
  };

  const handleChangeData = (e: any, data: any) => {
    const { name, value, checked, type } = data;

    const newValue = typeof checked === "boolean" ? checked : value;

    setFormState((prev) => {
      const updated = {
        ...prev,
        [name]: newValue,
      };

      if (name === "depend_on_experience") {
        if (checked) {
          updated.conditions = prev.conditions?.length
            ? prev.conditions
            : [{ experience_years: 0, max_leave_days: 0 }];
        } else {
          updated.conditions = [];
        }
      }

      if (name === "advance_notice" && !checked) {
        updated.threshold_advance_day = 0;
      }

      if (name === "allow_retroactive_leave" && !checked) {
        updated.threshold_retroactive_day = 0;
      }

      if (name === "restart_counting_every_year" && !checked) {
        updated.reset_month = undefined;
        updated.reset_day = 1;
        updated.maximum_accrued_leave_days = 0;
      }

      return updated;
    });

    // props.setProp()
  };

  const handleSave = () => {
    const parseFormState: LeaveTypeListApiType = {
      ...formState,
      conditions: (formState.conditions || []).map((cond) => ({
        ...cond,
        experience_years: safeParseFloat(cond.experience_years as string),
      })),
    };

    props.runSequence({
      sequence: "LeavePolicy",
      action: "SAVE",
      saveLeaveType: parseFormState,
      isCreate: !formState?.id,
    });
  };

  const handleNumberInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (["E", "e", "+", "-", "."].includes(e.key)) {
      e.preventDefault();
    }
  };

  const dateOptions = useMemo(() => {
    const selectedMonth = Number(formState?.reset_month);
    let maxDate = 31;

    if ([4, 6, 9, 11].includes(selectedMonth)) {
      maxDate = 30;
    } else if (selectedMonth === 2) {
      maxDate = 29;
    }

    return Array.from({ length: maxDate }, (_, i) => {
      const day = (i + 1);
      return { key: day, value: day, text: day };
    });
  }, [formState?.reset_month]);

  const safeParseFloat = (value: string | number | null | undefined) => {
    const num = parseFloat(String(value));
    
    return Number.isFinite(num) ? num : 0;
  };

  return (
    <div style={{ width: "100%", height: "100%", padding: "10px" /*border: "solid red 1px"*/ }}>
      <Form style={{ marginLeft: "1rem" }}>
        <FormGroup /*style={{ marginBottom: "1rem" }}*/>
          <label id="CardLeaveSettingsPanel-Label-Panel" style={{ fontSize: "18px", fontWeight: "bold" }}>Leave Type Settings</label>
        </FormGroup>
        {/* Type Name Section */}
        <FormGroup inline style={{ alignItems: "flex-start" }}>
          <FormField inline width={2} style={{ paddingTop: "8px" }}>
            <label style={{ fontSize: "16px", fontWeight: "bold" }}>
              Name <span style={{ color: "red" }}>*</span>
            </label>
          </FormField>
          <FormField inline width={6}>
            <Input
              id="CardLeaveSettingsPanel-Input-Name"
              style={{ width: "100%" }}
              value={formState?.name || ""}
              name="name"
              onChange={handleChangeData}
            />
          </FormField>
        </FormGroup>
        {/* Max Days Section */}
        <FormGroup inline style={{ alignItems: "flex-start" }}>
          <FormField inline width={4} style={{ paddingTop: "8px" }}>
            <label style={{ fontSize: "16px", fontWeight: "bold" }}>Maximum Days per Year</label>
          </FormField>
          <FormField inline width={4}>
            <Input
              id="CardLeaveSettingsPanel-Input-MaxLeaveDays"
              type="number"
              min={0}
              style={{ width: "100%" }}
              value={formState?.max_leave_days || ""}
              name="max_leave_days"
              onChange={handleChangeData}
              onKeyDown={handleNumberInput}
            />
          </FormField>
        </FormGroup>
        {/* Depend on Exp Section */}
        <FormGroup inline style={{ alignItems: "flex-start" }}>
          <FormField inline width={6} style={{ minWidth: "250px" }}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={{ fontSize: "16px", fontWeight: "bold" }}>Depend on Experience</label>
              <span style={{ fontSize: "12px", color: "gray" }}>
                Enable if leave can be different in each employee
              </span>
            </div>
          </FormField>
          <FormField inline style={{ paddingTop: "8px" }}>
            <Checkbox
              id="CardLeaveSettingsPanel-Checkbox-DependOnExp"
              toggle
              checked={formState?.depend_on_experience || false}
              name="depend_on_experience"
              onChange={handleChangeData}
            />
          </FormField>
          {formState?.depend_on_experience && (
            <FormField inline style={{ flexGrow: 1 }}>
              <DependOnExpDetail
                value={formState?.conditions || []}
                onChange={(newValue: LeaveTypeConditionType[]) =>
                  setFormState((prev) => ({
                    ...prev,
                    conditions: newValue,
                  }))
                }
              />
            </FormField>
          )}
        </FormGroup>
        {/* Restrict For Gender Section */}
        <FormGroup inline style={{ alignItems: "flex-start" }}>
          <FormField inline width={4} style={{ paddingTop: "8px" }}>
            <label style={{ fontSize: "16px", fontWeight: "bold" }}>Restrict For Gender</label>
          </FormField>
          <FormField inline width={4}>
            <div style={{ width: "100%" }}>
              <Dropdown
                id="CardLeaveSettingsPanel-Dropdown-RestrictGender"
                fluid
                selection
                value={formState?.restrict_for_gender}
                name="restrict_for_gender"
                onChange={handleChangeData}
                options={RestrictGenderOptions}
              />
            </div>
          </FormField>
        </FormGroup>
        {/* Attachment Required Section */}
        <FormGroup inline style={{ alignItems: "flex-start" }}>
          <FormField inline width={6} style={{ minWidth: "250px" }}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={{ fontSize: "16px", fontWeight: "bold" }}>
                Attachment Document Required
              </label>
              <span style={{ fontSize: "12px", color: "gray" }}>
                Enable if document is required for this leave type
              </span>
            </div>
          </FormField>
          <FormField inline style={{ paddingTop: "8px" }}>
            <Checkbox
              id="CardLeaveSettingsPanel-Checkbox-AttachRequired"
              toggle
              checked={formState?.attachment_required || false}
              name="attachment_required"
              onChange={handleChangeData}
            />
          </FormField>
        </FormGroup>
        {/* Advance Notice */}
        <FormGroup inline style={{ alignItems: "flex-start" }}>
          <FormField inline width={6} style={{ minWidth: "250px" }}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={{ fontSize: "16px", fontWeight: "bold" }}>Advance Notice</label>
              <span style={{ fontSize: "12px", color: "gray" }}>
                Enable if leave can be applied before the fact
              </span>
            </div>
          </FormField>
          <FormField inline style={{ paddingTop: "8px" }}>
            <Checkbox
              id="CardLeaveSettingsPanel-Checkbox-AdvanceNotice"
              toggle
              checked={formState?.advance_notice || false}
              name="advance_notice"
              onChange={handleChangeData}
            />
          </FormField>
          {formState?.advance_notice && (
            <FormGroup inline style={{ alignItems: "flex-start" }}>
              <FormField inline style={{ paddingTop: "8px" }}>
                <label>Days Threshold</label>
              </FormField>
              <FormField inline>
                <Input
                  id="CardLeaveSettingsPanel-Input-ThresholdAdvanceNotice"
                  disabled={!formState?.advance_notice}
                  value={formState?.threshold_advance_day || ""}
                  name="threshold_advance_day"
                  onChange={handleChangeData}
                  onKeyDown={handleNumberInput}
                  type="number"
                  min={0}
                />
              </FormField>
            </FormGroup>
          )}
        </FormGroup>
        {/* Allow Retroactive Section */}
        <FormGroup inline style={{ alignItems: "flex-start" }}>
          <FormField inline width={6} style={{ minWidth: "250px" }}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={{ fontSize: "16px", fontWeight: "bold" }}>
                Allow Retroactive Leave
              </label>
              <span style={{ fontSize: "12px", color: "gray" }}>
                Enable if leave can be applied after the fact
              </span>
            </div>
          </FormField>
          <FormField inline style={{ paddingTop: "8px" }}>
            <Checkbox
              id="CardLeaveSettingsPanel-Checkbox-AllowRetroactive"
              toggle
              checked={formState?.allow_retroactive_leave || false}
              name="allow_retroactive_leave"
              onChange={handleChangeData}
            />
          </FormField>
          {formState?.allow_retroactive_leave && (
            <FormGroup inline style={{ alignItems: "flex-start" }}>
              <FormField inline style={{ paddingTop: "8px" }}>
                <label>Days Threshold</label>
              </FormField>
              <FormField>
                <Input
                  id="CardLeaveSettingsPanel-Input-ThresholdRetroactive"
                  disabled={!formState?.allow_retroactive_leave}
                  value={formState?.threshold_retroactive_day || ""}
                  name="threshold_retroactive_day"
                  onChange={handleChangeData}
                  onKeyDown={handleNumberInput}
                  type="number"
                  min={0}
                />
              </FormField>
            </FormGroup>
          )}
        </FormGroup>
        {/* Allow Half-day Section */}
        <FormGroup inline style={{ alignItems: "flex-start" }}>
          <FormField inline width={6} style={{ minWidth: "250px" }}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={{ fontSize: "16px", fontWeight: "bold" }}>Allow Half-Day Leave</label>
              <span style={{ fontSize: "12px", color: "gray" }}>
                Enable if half-day leave is permitted
              </span>
            </div>
          </FormField>
          <FormField inline style={{ paddingTop: "8px" }}>
            <Checkbox
              id="CardLeaveSettingsPanel-Checkbox-AllowHalfDay"
              toggle
              checked={formState?.allow_half_day_leave || false}
              name="allow_half_day_leave"
              onChange={handleChangeData}
            />
          </FormField>
        </FormGroup>
        {/* Restart Section */}
        <FormGroup inline style={{ alignItems: "flex-start" }}>
          <FormField inline width={6} style={{ minWidth: "250px" }}>
            <div style={{ display: "flex", flexDirection: "column" }}>
              <label style={{ fontSize: "16px", fontWeight: "bold" }}>
                Restart Counting Every Year
              </label>
              <span style={{ fontSize: "12px", color: "gray", width: "240px" }}>
                Enable if the maximum leave day is start new counting by determined month-date
              </span>
            </div>
          </FormField>
          <FormField inline style={{ paddingTop: "8px" }}>
            <Checkbox
              id="CardLeaveSettingsPanel-Checkbox-RestartCounting"
              toggle
              checked={formState?.restart_counting_every_year || false}
              name="restart_counting_every_year"
              onChange={handleChangeData}
            />
          </FormField>
          {formState?.restart_counting_every_year ? (
            <FormGroup inline style={{ alignItems: "flex-start" }}>
              <FormField inline style={{ paddingTop: "8px" }}>
                <label>Month</label>
              </FormField>
              <FormField inline>
                <div style={{ width: "150px" }}>
                  <Dropdown
                    id="CardLeaveSettingsPanel-Dropdown-ResetMonth"
                    fluid
                    selection
                    disabled={!formState?.restart_counting_every_year}
                    value={formState?.reset_month || ""}
                    name="reset_month"
                    onChange={handleChangeData}
                    options={MONTH_EN_OPTION}
                  />
                </div>
              </FormField>
              <FormField inline style={{ paddingTop: "8px" }}>
                <label>Date</label>
              </FormField>
              <FormField inline>
                <div style={{ width: "80px" }}>
                  <Dropdown
                    id="CardLeaveSettingsPanel-Dropdown-ResetDay"
                    fluid
                    selection
                    disabled={!formState?.restart_counting_every_year}
                    value={formState?.reset_day || ""}
                    name="reset_day"
                    onChange={handleChangeData}
                    options={dateOptions}
                  />
                </div>
              </FormField>
            </FormGroup>
          ) : (
            <FormGroup inline style={{ alignItems: "flex-start" }}>
              <FormField inline style={{ paddingTop: "8px" }}>
                <label>
                  Maximum Accrued Leave Day <span style={{ color: "red" }}>*</span>
                </label>
              </FormField>
              <FormField inline>
                <Input
                  id="CardLeaveSettingsPanel-Input-MaxAccruedLeaveDays"
                  value={formState?.maximum_accrued_leave_days || ""}
                  name="maximum_accrued_leave_days"
                  onChange={handleChangeData}
                  onKeyDown={handleNumberInput}
                  type="number"
                  min={0}
                />
              </FormField>
            </FormGroup>
          )}
        </FormGroup>
        {/* Button Section */}
        <FormGroup inline style={{ display: "flex", justifyContent: "flex-end" }}>
          <FormField inline>
            <ButtonLoadCheck
              id="CardLeaveSettingsPanel-ButtonLoadCheck-Save"
              // func
              setProp={props.setProp}
              onClick={handleSave}
              // data
              paramKey={`${CARD_NAME}_SAVE`}
              buttonLoadCheck={props.buttonLoadCheck?.[`${CARD_NAME}_SAVE`]}
              // config
              color="green"
              title="SAVE"
              name="SAVE"
              size="medium"
            />
          </FormField>
          <FormField inline>
            <Button
              id="CardLeaveSettingsPanel-Button-Close"
              color="red"
              onClick={handleClose}>
              CLOSE
            </Button>
          </FormField>
        </FormGroup>
      </Form>
    </div>
  );
};

/**========================================================================
 **                              COMPONENT
 *========================================================================**/

const DependOnExpDetail = (props: DependOnExpDetailProps) => {
  const handleChange = (index: number, field: keyof LeaveTypeConditionType, newValue: string) => {
    const updated = [...props.value];

    if (field === "experience_years") {
      updated[index][field] = newValue as any;
    } else {
      updated[index][field] = parseInt(newValue);
    }

    props.onChange(updated);
  };

  const handleAdd = () => {
    const updated = [
      ...props.value,
      {
        experience_years: "0",
        max_leave_days: 0,
      },
    ];

    props.onChange(updated);
  };

  const handleDelete = (index: number) => {
    const updated = [...props.value];
    updated.splice(index, 1);

    props.onChange(updated);
  };

  const handleNumberInput = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (["E", "e", "+", "-"].includes(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
      {(props.value || []).map((item: LeaveTypeConditionType, index: number) => (
        <div
          key={index}
          style={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
          }}
        >
          <label style={{ fontWeight: "bold" }}>{">"}</label>
          <Input
            // type="number"
            // step="0.1"
            // min={0}
            id={`CardLeaveSettingsPanel-Input-ExpYear-${index}`}
            value={item.experience_years || ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange(index, "experience_years", e.target.value)
            }
            onKeyDown={handleNumberInput}
            style={{ width: "6rem" }}
          />
          <label style={{ fontWeight: "bold" }}>Year(s) =</label>
          <Input
            id={`CardLeaveSettingsPanel-Input-MaxLeaveDay-${index}`}
            type="number"
            min={0}
            value={item.max_leave_days || ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              handleChange(index, "max_leave_days", e.target.value)
            }
            onKeyDown={handleNumberInput}
            style={{ width: "6rem" }}
          />
          <label style={{ fontWeight: "bold" }}>Day(s)/yr</label>

          {/* Button Section */}
          {index === 0 ? (
            <Button id={`CardLeaveSettingsPanel-Button-Add-${index}`} basic icon size="mini" color="blue" type="button" onClick={handleAdd}>
              <Icon name="plus" />
            </Button>
          ) : (
            <Button
              id={`CardLeaveSettingsPanel-Button-Delete-${index}`}
              basic
              icon
              size="mini"
              color="red"
              type="button"
              onClick={() => handleDelete(index)}
            >
              <Icon name="minus" />
            </Button>
          )}
        </div>
      ))}
    </div>
  );
};

export default CardLeaveSettingsPanel;
